import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { ref } from 'vue';
import { RESOURCE_TYPE_REDIRECT } from '~~/common/utils/urlResource';
import type { DeepPartial, Schema } from '~~/common/types/api';

export type UrlType = DeepPartial<Schema<'UrlModel'>>;

export type UrlResourceStoreType = {
  data: UrlType;
};

interface RedirectData extends UrlType {
  fromUrl: string;
}

interface UrlResourceRequestHandlers<T> {
  delCache: () => void;
  setCache: (newValue: T) => void;
  getCache: () => Promise<T>;
}

const promiseCache: Record<string, Promise<unknown>> = {};

export const useUrlResourceStore = defineStore('urlResource', () => {
  const data = ref<UrlType>({});
  const _serializableCache = ref<Record<string, unknown>>({});
  const modalData = ref<UrlType>({});
  const redirectData = ref<RedirectData | null>(null);

  function urlResourceRequest<T> (cacheKey: string, cb: () => Promise<T>): UrlResourceRequestHandlers<T> {
    return {
      delCache: () => {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete _serializableCache.value[cacheKey];
      },
      setCache: (newValue: T) => {
        _serializableCache.value[cacheKey] = newValue;
      },
      getCache: async () => {
        if (cacheKey in _serializableCache.value) {
          return _serializableCache.value[cacheKey] as T;
        } else if (promiseCache[cacheKey]) {
          return await (promiseCache[cacheKey] as Promise<T>);
        } else {
          promiseCache[cacheKey] = (async () => {
            return await cb();
          })().finally(() => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete promiseCache[cacheKey];
          });
          return promiseCache[cacheKey] as Promise<T>;
        }
      },
    };
  }

  // Performs the (deduped) `/url-resource` request without touching render state.
  // Shared by `fetchByUrl` and the routeHistory middleware so a single SSR request
  // is reused instead of firing the same lookup twice.
  async function resolveByUrl (url: string): Promise<UrlType> {
    const { $api } = useNuxtApp();
    const cacheKey = `/url-resource?slug=${url}`;
    const { getCache, setCache, delCache } = urlResourceRequest<UrlType>(cacheKey, () => {
      return $api.$get<UrlType>('/url-resource', {
        params: {
          slug: url,
        },
      });
    });
    const result = await getCache();
    (import.meta.server
      ? setCache(result)
      : delCache()
    );
    return result || {};
  }

  async function fetchByUrl (url: string): Promise<void> {
    const { $logger } = useNuxtApp();
    try {
      const result = await resolveByUrl(url);

      if (result && result.type === RESOURCE_TYPE_REDIRECT && url !== result.slug) {
        setRedirectData({
          ...result,
          fromUrl: url,
        });
      } else {
        setRedirectData(null);
      }

      setData(result || {});
    } catch (err) {
      $logger.error(err);
      setData({});
    }
  }

  async function fetchFlipbookUrl (resourceId: string): Promise<string> {
    const { $api, $logger } = useNuxtApp();
    try {
      const cacheKey = `/catalog/${resourceId}`;
      const { getCache, setCache, delCache } = urlResourceRequest<{ flipbookUrl?: string }>(cacheKey, () => {
        return $api.$get<{ flipbookUrl?: string }>(cacheKey);
      });
      const result = await getCache();
      import.meta.server
        ? setCache(result)
        : delCache()
      ;
      return (typeof result?.flipbookUrl === 'string'
        ? result.flipbookUrl
        : ''
      );
    } catch (err) {
      $logger.error(err);
      return '';
    }
  }

  async function fetchModalUrlResourceByUrl (url: string): Promise<void> {
    const { $api, $logger } = useNuxtApp();
    try {
      const cacheKey = `/url-resource?slug=${url}`;
      const { getCache } = urlResourceRequest<UrlType>(cacheKey, () => {
        return $api.$get<UrlType>('/url-resource', {
          params: {
            slug: url,
          },
        });
      });
      setModalData(await getCache() || {});
    } catch (err) {
      $logger.error(err);
      setModalData({});
    }
  }

  function setData (newData: UrlType): void {
    data.value = newData;

    if (import.meta.server) {
      if (!global.storeHelper) {
        global.storeHelper = { urlResourceStoreData: newData, cdExternalId: '', locale: '', isEcomUser: false };
      } else {
        global.storeHelper.urlResourceStoreData = newData;
      }
    }
  }

  function setModalData (newData: UrlType): void {
    modalData.value = newData;
  }

  function setRedirectData (newData: RedirectData | null): void {
    redirectData.value = newData;
  }

  return {
    data,
    _serializableCache,
    modalData,
    redirectData,
    resolveByUrl,
    fetchByUrl,
    fetchFlipbookUrl,
    fetchModalUrlResourceByUrl,
    setData,
    setModalData,
    setRedirectData,
  };
});
